import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { bookmarkStep, getOnboardingState } from '@/api/onboarding'
import {
  activateChoosing,
  getShowcasePosts,
  ShowcaseApiError,
  WhatToPublish,
} from '@/api/showcase'
import { track, trackError } from '@/lib/analytics'

/**
 * Onboarding шаг — что публиковать (слайд 17 нового дизайна).
 *
 * Phase 1 — продолжение showcase-consent. Радио-выбор `whatToPublish`,
 * счётчики «N постов» подгружаются из B6 list (limit:1) для каждого режима.
 * После «Включить» вызывает activateChoosing с backfillRequested=true для
 * вариантов 'all' и 'long' (юзер согласился на публикацию архива) и
 * переходит на /onboarding/tariff.
 */
export function ShowcaseRulesPage() {
  const navigate = useNavigate()
  const { selectedChannel } = useChannel()
  const [choice, setChoice] = useState<WhatToPublish>('all')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalPosts, setTotalPosts] = useState<number | null>(null)
  // Если у юзера уже есть активный Профи (state.hasTariff или channel.paidUntilEpoch>now) —
  // витрина включается без шага оплаты. Логика идентична TariffPage.alreadyPaid.
  const [alreadyPaid, setAlreadyPaid] = useState(false)

  useEffect(() => {
    if (!selectedChannel?.uuid) return
    const channelPaidActive =
      selectedChannel.paidUntilEpoch &&
      new Date(selectedChannel.paidUntilEpoch).getTime() > Date.now()
    getOnboardingState()
      .then((s) => setAlreadyPaid(!!s.hasTariff || !!channelPaidActive))
      .catch(() => {
        if (channelPaidActive) setAlreadyPaid(true)
      })
  }, [selectedChannel?.uuid, selectedChannel?.paidUntilEpoch])

  // Один запрос: total из counts.published показывает сколько уже в Витрине,
  // но «всего постов канала» — у нас этого числа нет. Используем total из
  // /posts без фильтра как приблизительный ориентир (это все посты в реестре
  // Витрины, не сырой канал, но для онбординга достаточно).
  useEffect(() => {
    if (!selectedChannel?.uuid) return
    getShowcasePosts(selectedChannel.uuid, { limit: 1 })
      .then((res) => {
        const raw = res.countsByStatus
          ?? (res as unknown as { counts_by_status?: Record<string, number> }).counts_by_status
        if (raw) {
          const sum = Object.values(raw).reduce((a, b) => a + b, 0)
          setTotalPosts(sum)
        } else {
          setTotalPosts(res.total ?? null)
        }
      })
      .catch(() => setTotalPosts(null))
  }, [selectedChannel?.uuid])

  async function handleNext() {
    if (!selectedChannel?.uuid) return
    setSubmitting(true)
    setError(null)
    try {
      try {
        // Если Профи нет — backfill не запускаем сразу: backend сохранит правило,
        // а TariffPage после успешной оплаты перевызовет activateChoosing уже с
        // backfillRequested=true. Это защищает от «активной витрины без оплаты».
        await activateChoosing(selectedChannel.uuid, {
          whatToPublish: choice,
          backfillRequested: alreadyPaid && choice !== 'manual',
        })
      } catch (e) {
        // 409 — уже в active/choosing, не блокируем дальше
        if (!(e instanceof ShowcaseApiError && e.status === 409)) throw e
      }
      track('showcase_onboarding_rules', {
        whatToPublish: choice,
        channelUUID: selectedChannel.uuid,
        alreadyPaid,
        message: `правила публикации выбраны · ${choice}${alreadyPaid ? ' · skip-tariff' : ''}`,
      })
      // Уже Профи → витрина включается сразу, к тарифу не идём.
      // Иначе → стандартный шаг оплаты, после оплаты witrина активируется.
      if (alreadyPaid) {
        bookmarkStep('final', 'showcase')
        navigate('/onboarding/final')
      } else {
        bookmarkStep('tariff', 'showcase')
        navigate('/onboarding/tariff')
      }
    } catch (e) {
      trackError(e, 'showcase-onboarding-rules')
      setError(e instanceof Error ? e.message : 'Не удалось сохранить настройки')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      step="showcase-rules"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/showcase-consent')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-next" disabled={submitting} onClick={handleNext}>
            {submitting
              ? 'Включаем…'
              : alreadyPaid
                ? 'Включить витрину'
                : 'Включить витрину — к тарифу'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="sr-narrow">
        <p className="stage-eyebrow">
          <span className="stage-eyebrow-pill">Витрина</span>
          Какие посты публиковать
        </p>
        <h1 className="stage-title">
          Что покажем <em>в&nbsp;поиске</em>.
        </h1>
        <p className="stage-sub">
          AI-агент сам подготовит каждый пост к&nbsp;поиску. Выберите, какие посты канала попадут на&nbsp;витрину — потом это можно изменить.
        </p>

        <div className="sr-list">
          <RuleOption
            value="all"
            current={choice}
            title="Все посты"
            sub="Максимум охвата — публикуем всё"
            count={totalPosts}
            countLabel="постов"
            onPick={setChoice}
          />
          <RuleOption
            value="long"
            current={choice}
            title="Только длинные"
            sub="От 500 символов — содержательные посты лучше находят"
            count={totalPosts !== null ? Math.round(totalPosts * 0.55) : null}
            countLabel="постов"
            onPick={setChoice}
            approxNote
          />
          <RuleOption
            value="manual"
            current={choice}
            title="Выбрать вручную"
            sub="Отметите посты сами в разделе «Витрина»"
            count={null}
            countLabel="вручную"
            onPick={setChoice}
          />
        </div>

        <div className="sr-info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          <span>
            Первые читатели появятся через <b>1–2&nbsp;недели</b> — столько занимает попадание в&nbsp;поиск. Следить за&nbsp;прогрессом можно в&nbsp;разделе «Витрина».
          </span>
        </div>

        {error && <div className="sr-error">{error}</div>}
      </div>

      <style>{`
        .sr-narrow { max-width: 720px; }
        .sr-list { display: flex; flex-direction: column; gap: 10px; margin-top: 24px; }
        .sr-info {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-top: 18px;
          padding: 14px 16px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 12px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .sr-info svg { width: 17px; height: 17px; color: var(--tg-mute, #5b6678); flex-shrink: 0; margin-top: 1px; }
        .sr-error {
          margin-top: 14px;
          padding: 10px 14px;
          background: rgba(229, 70, 70, 0.1);
          border: 1px solid rgba(229, 70, 70, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
      `}</style>
    </OnboardingShell>
  )
}

function RuleOption({
  value, current, title, sub, count, countLabel, onPick, approxNote,
}: {
  value: WhatToPublish
  current: WhatToPublish
  title: string
  sub: string
  count: number | null
  countLabel: string
  onPick: (v: WhatToPublish) => void
  approxNote?: boolean
}) {
  const isOn = current === value
  return (
    <label className={`ro ${isOn ? 'on' : ''}`} onClick={() => onPick(value)}>
      <span className="ro-radio">
        {isOn && <span className="ro-radio-dot" />}
      </span>
      <span className="ro-body">
        <span className="ro-title">{title}</span>
        <span className="ro-sub">{sub}</span>
      </span>
      <span className="ro-count">
        <span className={`ro-count-v ${count === null ? 'muted' : ''}`}>
          {count !== null ? count.toLocaleString('ru-RU') : '—'}
          {approxNote && count !== null && <span className="ro-approx"> ≈</span>}
        </span>
        <span className="ro-count-l">{countLabel}</span>
      </span>

      <style>{`
        .ro {
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 16px;
          align-items: center;
          padding: 16px 20px;
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          cursor: pointer;
          background: var(--tg-surface, #fff);
        }
        .ro.on {
          border-color: var(--tg-blue, #0080ff);
          background: var(--tg-blue-tint-bg, #f4f8ff);
        }
        .ro-radio {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2px solid var(--tg-line-3, #d0d0d8);
          position: relative;
          flex-shrink: 0;
        }
        .ro.on .ro-radio { border-color: var(--tg-blue, #0080ff); }
        .ro-radio-dot {
          position: absolute;
          inset: 3px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
        }
        .ro-body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .ro-title { font-weight: 700; font-size: 15px; color: var(--tg-ink, #111); }
        .ro-sub { font-size: 13px; color: var(--tg-mute, #5b6678); }
        .ro-count { text-align: right; }
        .ro-count-v {
          font-weight: 800;
          font-size: 20px;
          letter-spacing: -0.02em;
          color: var(--tg-blue-press, #0052cc);
          display: block;
          font-variant-numeric: tabular-nums;
        }
        .ro-count-v.muted { color: var(--tg-mute, #5b6678); }
        .ro-approx { font-size: 11px; color: var(--tg-mute, #5b6678); font-weight: 600; }
        .ro-count-l { font-size: 11px; color: var(--tg-mute, #5b6678); }
      `}</style>
    </label>
  )
}
